import _defineProperty from "/vercel/path1/node_modules/next/dist/compiled/@babel/runtime/helpers/esm/defineProperty.js";
import React from "react";
var __jsx = React.createElement;
function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faChevronLeft } from "@fortawesome/pro-regular-svg-icons";
import { useAuth } from "@qure/qure-auth";
import { useSelectedPatientStore } from "@/stores/selected-patient";
import { useTaskLabels, useCreateReminder } from "@/hooks/tasks";
import { useWorkspaceUsers } from "@/hooks/use-workspaces";
import { LinkPatientRecordsModal } from "@/components/link-patient-records-modal";
import { showErrorToast } from "@/components/toast";
import { trackEvent, ANALYTICS_EVENTS } from "@/utils/analytics";
import { INITIAL_FORM_STATE, buildCreatePayload } from "./create-task-constants";
import { CreateTaskForm } from "./create-task-form";
import { CreateTaskFooter } from "./create-task-footer";
/** Full-panel create task form replacing the task listing when the user clicks +. */
export function CreateTask(_ref) {
  var _userInfo$default_wor, _userInfo$details$ema, _userInfo$details;
  var onCancel = _ref.onCancel,
    onSuccess = _ref.onSuccess,
    _ref$prefillState = _ref.prefillState,
    prefillState = _ref$prefillState === void 0 ? {} : _ref$prefillState;
  var _useTranslation = useTranslation(),
    t = _useTranslation.t;
  var _useAuth = useAuth(),
    userInfo = _useAuth.userInfo;
  var workspaceId = (_userInfo$default_wor = userInfo === null || userInfo === void 0 ? void 0 : userInfo.default_workspace) !== null && _userInfo$default_wor !== void 0 ? _userInfo$default_wor : null;
  var currentUserEmail = (_userInfo$details$ema = userInfo === null || userInfo === void 0 || (_userInfo$details = userInfo.details) === null || _userInfo$details === void 0 ? void 0 : _userInfo$details.email) !== null && _userInfo$details$ema !== void 0 ? _userInfo$details$ema : "";
  var hasPrefill = Object.keys(prefillState).length > 0;
  var patientUid = useSelectedPatientStore(function (s) {
    var _s$selectedPatient;
    return (_s$selectedPatient = s.selectedPatient) !== null && _s$selectedPatient !== void 0 && _s$selectedPatient.patient_uid ? String(s.selectedPatient.patient_uid) : null;
  });
  var _useState = useState(_objectSpread(_objectSpread({}, INITIAL_FORM_STATE), prefillState)),
    formState = _useState[0],
    setFormState = _useState[1];
  var _useState2 = useState(false),
    isLinkRecordsOpen = _useState2[0],
    setIsLinkRecordsOpen = _useState2[1];
  var didDefaultOwnerRef = useRef(false);
  var _useState3 = useState(undefined),
    linkRecordsTab = _useState3[0],
    setLinkRecordsTab = _useState3[1];
  var _useTaskLabels = useTaskLabels(workspaceId),
    _useTaskLabels$data = _useTaskLabels.data,
    taskLabels = _useTaskLabels$data === void 0 ? [] : _useTaskLabels$data;
  var _useWorkspaceUsers = useWorkspaceUsers(workspaceId),
    _useWorkspaceUsers$da = _useWorkspaceUsers.data,
    workspaceUsers = _useWorkspaceUsers$da === void 0 ? [] : _useWorkspaceUsers$da;
  var _useCreateReminder = useCreateReminder(patientUid),
    createReminder = _useCreateReminder.mutate,
    isSaving = _useCreateReminder.isLoading;
  var labelOptions = taskLabels.map(function (l) {
    return {
      value: String(l.id),
      label: l.name
    };
  });
  var ownerOptions = workspaceUsers.map(function (u) {
    return {
      value: String(u.id),
      label: u.name
    };
  });
  var isSaveDisabled = isSaving || !formState.title.trim() || !formState.remindAfter || !formState.ownedBy;
  var handleSave = function handleSave() {
    if (isSaveDisabled || !patientUid) return;
    createReminder(buildCreatePayload(formState), {
      onSuccess: onSuccess,
      onError: function onError() {
        showErrorToast(t("toast.create_task_failed", {
          defaultValue: "Failed to create task"
        }));
      }
    });
  };
  var patch = function patch(partial) {
    return setFormState(function (prev) {
      return _objectSpread(_objectSpread({}, prev), partial);
    });
  };
  useEffect(function () {
    if (!hasPrefill || didDefaultOwnerRef.current || formState.ownedBy || workspaceUsers.length === 0) {
      return;
    }
    var currentUser = workspaceUsers.find(function (u) {
      return u.email === currentUserEmail;
    });
    if (!currentUser) return;
    didDefaultOwnerRef.current = true;
    setFormState(function (prev) {
      return prev.ownedBy ? prev : _objectSpread(_objectSpread({}, prev), {}, {
        ownedBy: String(currentUser.id)
      });
    });
  }, [hasPrefill, workspaceUsers, formState.ownedBy, currentUserEmail]);
  return /*#__PURE__*/React.createElement("div", {
    className: "h-full flex flex-col bg-sds-base-default overflow-hidden"
  }, /*#__PURE__*/React.createElement("div", {
    className: "flex items-center gap-2 px-4 py-3 flex-shrink-0 border-b border-sds-base-default"
  }, /*#__PURE__*/React.createElement("button", {
    "data-testid": "create-task-back",
    "aria-label": t("button.back"),
    onClick: function onClick() {
      trackEvent(ANALYTICS_EVENTS.CREATE_TASK_BACK);
      onCancel();
    },
    className: "w-6 h-6 flex items-center justify-center rounded-sds-100 text-sds-base-secondary hover:bg-sds-base-secondary transition-colors"
  }, /*#__PURE__*/React.createElement(FontAwesomeIcon, {
    icon: faChevronLeft,
    className: "w-3.5 h-3.5"
  })), /*#__PURE__*/React.createElement("span", {
    className: "text-style-label-caps text-sds-base-tertiary"
  }, t("label.add_task"))), /*#__PURE__*/React.createElement(CreateTaskForm, {
    formState: formState,
    onChangeTitle: function onChangeTitle(v) {
      return patch({
        title: v
      });
    },
    onChangeContext: function onChangeContext(v) {
      return patch({
        context: v
      });
    },
    onChangeDate: function onChangeDate(v) {
      return patch({
        remindAfter: v
      });
    },
    onChangeOwner: function onChangeOwner(v) {
      return patch({
        ownedBy: v
      });
    },
    onChangeLabel: function onChangeLabel(v) {
      return patch({
        labelId: v
      });
    },
    labelOptions: labelOptions,
    ownerOptions: ownerOptions,
    onOpenLinkRecords: function onOpenLinkRecords() {
      setLinkRecordsTab(undefined);
      setIsLinkRecordsOpen(true);
    },
    onViewRecords: function onViewRecords(tab) {
      setLinkRecordsTab(tab);
      setIsLinkRecordsOpen(true);
    }
  }), /*#__PURE__*/React.createElement(CreateTaskFooter, {
    isSaveDisabled: isSaveDisabled,
    isSaving: isSaving,
    onCancel: onCancel,
    onSave: handleSave
  }), /*#__PURE__*/React.createElement(LinkPatientRecordsModal, {
    show: isLinkRecordsOpen,
    initialTab: linkRecordsTab,
    onClose: function onClose() {
      return setIsLinkRecordsOpen(false);
    },
    onLink: function onLink(resources) {
      patch({
        linkedResources: resources
      });
      setIsLinkRecordsOpen(false);
    },
    linkedRes: formState.linkedResources
  }));
}